import { A } from '@adornis/base/env-info.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field, Mutation, Subscription } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { context, publishFilter } from '@adornis/baseql/server/context.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { Names } from '@ils/static/names.js';
import { DateTime } from 'luxon';
import { from, map, switchMap } from 'rxjs';

@Entity()
export class OnlineStatus extends MongoEntity {
  static override _class = Names.OnlineStatus._class;
  static override _collectionName = Names.OnlineStatus._collectionName;

  @validate(nonOptional())
  @Field(type => ID)
  userID!: string;

  @Field(type => DateTime, { default: v => v ?? DateTime.now() })
  createdAt!: DateTime;

  @Subscription(type => [ID])
  static subscribeOnlineUsers() {
    return () => {
      return from(publishFilter(this._class)).pipe(
        switchMap(pF => {
          if (!pF) throw new Error('No publish filter defined for OnlineStatus, please define a publish filter');
          return getCollectionHandle<OnlineStatus>(this._collectionName)
            .watchQuery(pF)
            .pipe(map(onlineStatus => onlineStatus.map(oS => oS.userID)));
        }),
      );
    };
  }

  @Mutation(type => Boolean)
  static ensureUserIsOnline() {
    return async () => {
      if (context.serverContext) throw new Error('Cannot call ensureUserIsOnline from server');
      if (!context.userID) return false;
      await this.addOnlineStatus(context.userID);
      return true;
    };
  }

  @Mutation(type => Boolean)
  static userLogout() {
    return async () => {
      if (context.serverContext) throw new Error('Cannot call ensureUserIsOnline from server');
      if (!context.userID) throw new Error('Cannot call ensureUserIsOnline if user is already logged out');
      await this.removeOnlineStatus(context.userID);
      return true;
    };
  }

  static async addOnlineStatus(userID: string) {
    if (!userID) return;
    const col = await getRawCollection<OnlineStatus>(this._collectionName);
    const existing = await col.findOne({ userID });
    if (existing) {
      // refresh
      return col.updateOne({ _id: existing._id }, { $set: { createdAt: DateTime.now() } });
    }

    return col.insertOne(
      new OnlineStatus({ _id: A.getGloballyUniqueID(), userID, createdAt: DateTime.now() }).toJSON(),
    );
  }

  static async removeOnlineStatus(userID: string) {
    if (!userID) return;
    const col = await getRawCollection<OnlineStatus>(this._collectionName);
    const existing = await col.findOne({ userID });
    if (existing) return col.deleteOne({ _id: existing._id });
  }
}
